/**
 * ViewNav — Sidebar section [2]: `nav` slot.
 *
 * Two-button toggle between the main stage views:
 *   - Lista  → home route (AuthorListView)
 *   - Grafo  → graph route (InfluenceGraphView)
 *
 * Listens to `stage:route-changed` from StageRouter to keep its active
 * state in sync when navigation is triggered elsewhere (e.g., clicking an
 * author card routes to author detail, which we treat as a sub-state of
 * the list view).
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';

type NavRoute = 'home' | 'graph' | 'concept-map';

export class ViewNav extends BaseSection {
  readonly sectionId: SectionId = 'nav';
  readonly sectionTitle = 'Navigazione viste';

  private listBtn!: HTMLButtonElement;
  private graphBtn!: HTMLButtonElement;
  private conceptMapBtn!: HTMLButtonElement;

  protected async onInit(): Promise<void> {
    this.element.classList.add('view-nav');
    this.element.innerHTML = `
      <div class="view-nav-inner">
        <button type="button" class="view-nav-btn is-active" data-route="home">
          <span class="view-nav-icon" aria-hidden="true">☰</span>
          <span>Lista</span>
        </button>
        <button type="button" class="view-nav-btn" data-route="graph">
          <span class="view-nav-icon" aria-hidden="true">✦</span>
          <span>Grafo</span>
        </button>
        <button type="button" class="view-nav-btn" data-route="concept-map">
          <span class="view-nav-icon" aria-hidden="true">◎</span>
          <span>Mappa concetti</span>
        </button>
      </div>
    `;

    this.listBtn = this.element.querySelector<HTMLButtonElement>('[data-route="home"]')!;
    this.graphBtn = this.element.querySelector<HTMLButtonElement>('[data-route="graph"]')!;
    this.conceptMapBtn = this.element.querySelector<HTMLButtonElement>('[data-route="concept-map"]')!;

    this.addEventListener(this.listBtn, 'click', () => {
      this.setActive('home');
      this.context.bus.emit('navigate:home', {});
    });
    this.addEventListener(this.graphBtn, 'click', () => {
      this.setActive('graph');
      this.context.bus.emit('navigate:graph', {});
    });
    this.addEventListener(this.conceptMapBtn, 'click', () => {
      this.setActive('concept-map');
      this.context.bus.emit('navigate:concept-map', {});
    });

    this.addCleanup(
      this.context.bus.on<{ route: string }>('stage:route-changed', ({ route }) => {
        if (route === 'home' || route === 'author') this.setActive('home');
        else if (route === 'graph') this.setActive('graph');
        else if (route === 'concept-map') this.setActive('concept-map');
      }),
    );
  }

  private setActive(route: NavRoute): void {
    this.listBtn.classList.toggle('is-active', route === 'home');
    this.graphBtn.classList.toggle('is-active', route === 'graph');
    this.conceptMapBtn.classList.toggle('is-active', route === 'concept-map');
  }
}
